import React, { useCallback, useMemo, useState } from 'react';
import type Transit from '../../../../domain/transit/transit';
import type { Tab } from '../../../../domain/select_store';
import type SelectStore from '../../../../domain/select_store';

import SelectTransportBoxForeignTemplate from './transport_box_foreign.template';
import DomesticTransport from './domestic';

// Internal props type
interface TransportBoxProps {
  handleClickTab?: (tab: Tab) => void;
  handleSelect?: (t: Transit, opts?: { changeable?: boolean; domesticAirPriceIndex?: number }) => void;
  transport: Transit;
  inList: boolean;
  selected: boolean;
  domesticAirPriceIndex: number;
  tab?: 'outword' | 'homeword' | 'packageOutword' | 'packageHomeword' | number;
  store: SelectStore;
  disabled?: boolean;
  isRequestForm?: boolean;
  candidateCount?: number;
}

const TransportBox: React.FC<TransportBoxProps> = ({
  handleClickTab,
  handleSelect,
  transport,
  inList,
  selected,
  domesticAirPriceIndex,
  tab,
  store,
  disabled,
  isRequestForm,
  candidateCount = 0
}) => {
  const [loading, setLoading] = useState(false);

  const handleDomesticAirPriceChange = useCallback(
    (index: number) => {
      if (!transport.prices || index < 0 || index >= transport.prices.length) return;
      if (handleSelect) {
        const selectedPrice = transport.prices[index];
        handleSelect(transport, {
          changeable: selectedPrice.ticket_category === 'changeable',
          domesticAirPriceIndex: index
        });
      }
    },
    [handleSelect, transport]
  );

  const handleChange = useCallback(async () => {
    if (inList) {
      if (handleSelect) {
        setLoading(true);
        try {
          await handleSelect(transport);
        } finally {
          setLoading(false);
        }
      }
    } else if (tab !== undefined) {
      if (handleClickTab) handleClickTab(selected ? 'none' : tab);
    }
  }, [inList, handleSelect, transport, tab, handleClickTab, selected]);

  const isDomestic = useMemo(() => store.result.items.some(i => i.isDomestic), [store.result.items]);

  return isDomestic ? (
    <DomesticTransport
      transport={transport}
      inList={inList}
      selected={selected}
      domesticAirPriceIndex={domesticAirPriceIndex}
      loading={loading}
      handleDomesticAirPriceChange={handleDomesticAirPriceChange}
      handleChange={handleChange}
      store={store}
      isRequestForm={isRequestForm || false}
      disabled={disabled || false}
      candidateCount={candidateCount}
    />
  ) : (
    <SelectTransportBoxForeignTemplate
      transport={transport}
      inList={inList}
      selected={selected}
      domesticAirPriceIndex={domesticAirPriceIndex}
      loading={loading}
      handleDomesticAirPriceChange={handleDomesticAirPriceChange}
      handleChange={handleChange}
      store={store}
      isRequestForm={isRequestForm || false}
      disabled={disabled || false}
      candidateCount={candidateCount}
    />
  );
};

export default TransportBox;
